import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MessageCircle } from "lucide-react";
import { FOOTER } from "../constants/testIds";
import { BRAND, WHATSAPP_DISPLAY, WHATSAPP_NUMBER } from "../constants/site";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { useCategories } from "../hooks/useCatalog";

// Icône TikTok (lucide-react n'en fournit pas) — SVG inline simple.
const TikTokIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
);

// Libellés propres pour le footer (les labels API restent la référence admin).
const CATEGORY_LABELS = {
    "coques": "Coques iPhone",
    "coques-samsung": "Coques Samsung",
    "coques-airpods": "Coques AirPods",
    "coque-personnalisee": "Coque personnalisée",
    "verre-trempe-hydrogel": "Verre trempé & hydrogel",
    "chargeurs": "Chargeurs",
    "cables-hubs": "Câbles & hubs",
    "power-bank": "Power Banks",
    "accessoires-voiture": "Accessoires voiture",
    "accessoires-audio-gadgets": "Audio & gadgets",
};

const NAV_LINKS = [
    { label: "Accueil", to: "/" },
    { label: "Produits", to: "/produits" },
    { label: "Offres", to: "/offres" },
    { label: "FAQ", to: "/#faq" },
];

// Marques distribuées — texte simple (le catalogue ne supporte pas
// la recherche par URL : pas de liens, nuage compact).
const BRANDS = [
    "Santa Barbara",
    "Puloka",
    "Casetify",
    "Pitaka",
    "Rimowa",
    "Nimmy",
    "Rhode",
    "Apple",
    "Samsung",
    "Mi",
    "Oraimo",
    "UGREEN",
    "KieSelect",
    "GOUI",
    "Earldom",
];

const Footer = () => {
    const { data: settings } = useSiteSettings();
    const { data: categories = [] } = useCategories();
    const brandName = settings?.brand_name || BRAND.name;
    const brandEmail = settings?.brand_email || BRAND.email;
    const whatsappDisplay = settings?.whatsapp_display || WHATSAPP_DISPLAY;
    const whatsappNumber = settings?.whatsapp_number || WHATSAPP_NUMBER;

    const activeCategories = (categories || []).filter((c) => c.active !== false);

    return (
        <footer
            data-testid={FOOTER.root}
            className="relative bg-[#0B0B0D] text-white pt-10 pb-6 overflow-hidden"
        >
            <div className="absolute inset-0 dp-grid-lines-dark opacity-30" />
            <div className="relative max-w-[1320px] mx-auto px-6 md:px-10">
                {/* Brand row */}
                <div className="flex flex-wrap items-center justify-between gap-4 pb-7 border-b border-white/10">
                    <Link to="/" className="flex items-center gap-2.5 group">
                        <svg width="28" height="28" viewBox="0 0 64 64">
                            <defs>
                                <linearGradient id="ftLogo" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor="#d4af37" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                </linearGradient>
                            </defs>
                            <rect x="2" y="2" width="60" height="60" rx="14" fill="url(#ftLogo)" />
                            <text
                                x="32"
                                y="45"
                                textAnchor="middle"
                                fill="white"
                                fontFamily="Outfit, Arial, sans-serif"
                                fontWeight="700"
                                fontSize="28"
                            >
                                AX
                            </text>
                        </svg>
                        <span className="font-display text-[18px] font-semibold tracking-tight">
                            {brandName}
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <a
                            href="https://instagram.com/atelier_ax.fes"
                            target="_blank"
                            rel="noopener"
                            aria-label="Instagram — Atelier AX (nouvel onglet)"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#d4af37]/60 hover:text-[#d4af37]"
                        >
                            <Instagram className="w-4 h-4" />
                        </a>
                        <a
                            href="https://tiktok.com/@atelier.ax"
                            target="_blank"
                            rel="noopener"
                            aria-label="TikTok — Atelier AX (nouvel onglet)"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#d4af37]/60 hover:text-[#d4af37]"
                        >
                            <TikTokIcon className="w-4 h-4" />
                        </a>
                    </div>
                </div>

                {/* 4 colonnes : catégories / navigation / contact / marques */}
                <div className="grid gap-8 py-8 sm:grid-cols-2 lg:grid-cols-4">
                    <nav aria-label="Catégories">
                        <div className="dp-eyebrow !text-white/50">Catégories</div>
                        <ul className="mt-4 grid grid-cols-1 gap-y-2 text-[13px]">
                            {activeCategories.map((c) => (
                                <li key={c.id}>
                                    <Link
                                        to={`/categorie/${encodeURIComponent(c.id)}`}
                                        className="text-white/65 hover:text-[#d4af37] transition-colors"
                                    >
                                        {CATEGORY_LABELS[c.id] || c.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav aria-label="Navigation">
                        <div className="dp-eyebrow !text-white/50">Navigation</div>
                        <ul className="mt-4 space-y-2 text-[13px]">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        className="text-white/65 hover:text-[#d4af37] transition-colors"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <div className="dp-eyebrow !text-white/50">Contact</div>
                        <ul className="mt-4 space-y-2.5 text-[13px] text-white/65">
                            <li>
                                <a
                                    href={`https://wa.me/${whatsappNumber}`}
                                    target="_blank"
                                    rel="noopener"
                                    className="inline-flex items-center gap-2 hover:text-[#d4af37] transition-colors"
                                >
                                    <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                                    WhatsApp : {whatsappDisplay}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`mailto:${brandEmail}`}
                                    className="inline-flex items-center gap-2 hover:text-[#d4af37] transition-colors"
                                >
                                    <Mail className="w-3.5 h-3.5 text-[#d4af37]" />
                                    {brandEmail}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://instagram.com/atelier_ax.fes"
                                    target="_blank"
                                    rel="noopener"
                                    className="inline-flex items-center gap-2 hover:text-[#d4af37] transition-colors"
                                >
                                    <Instagram className="w-3.5 h-3.5 text-[#d4af37]" />
                                    @atelier_ax.fes
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://tiktok.com/@atelier.ax"
                                    target="_blank"
                                    rel="noopener"
                                    className="inline-flex items-center gap-2 hover:text-[#d4af37] transition-colors"
                                >
                                    <TikTokIcon className="w-3.5 h-3.5 text-[#d4af37]" />
                                    @atelier.ax
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div aria-label="Marques">
                        <div className="dp-eyebrow !text-white/50">Marques</div>
                        <ul className="mt-4 flex flex-wrap gap-x-2 gap-y-1.5 text-[12px]">
                            {BRANDS.map((b) => (
                                <li
                                    key={b}
                                    className="rounded-full border border-white/10 px-2.5 py-0.5 text-white/55"
                                >
                                    {b}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="pt-5 border-t border-white/10 text-center text-[12px] text-white/40">
                    © {new Date().getFullYear()} {brandName} — Maroc
                </div>
            </div>
        </footer>
    );
};

export default Footer;
